import { fabric } from 'fabric';
import { Canvas, IEvent, Polygon } from 'fabric/fabric-impl';
import _ from 'lodash';
import { ObservableField } from 'shared/utils';
import history from './history';

/**
 * Правка точек многоугольника — по официальному демо Fabric 5 «Custom controls, polygon»
 * (https://fabric5.fabricjs.com/custom-controls-polygon): на каждую точку свой `fabric.Control`, при переносе одна из
 * прочих точек — якорь, чтобы фигура не съезжала. Отличие от демо: якорь держится через полную матрицу объекта, а не
 * через доли размера, — так верно и у отражённой, повёрнутой и растянутой фигуры.
 */

export const MIN_POLYGON_POINTS = 3;

/** Допуск попадания по точке и ребру, пиксели экрана. */
const HIT_TOLERANCE = 8;

export interface PolygonSize {
  width: number;
  height: number;
}

export interface PolygonEditOptions {
  /**
   * Рамка многоугольника сменилась (`width` / `height` без масштаба). Движок не знает о заливке фигуры: градиент в
   * пикселях объекта пересобирает вызывающий.
   */
  onResize?: (from: PolygonSize, to: PolygonSize) => void;
}

export type PolygonHit = { kind: 'point'; index: number } | { kind: 'edge'; index: number; point: fabric.Point };

/** Точка `index` многоугольника в координатах канвы (без зума и сдвига вида). */
export function polygonPointOnCanvas(polygon: Polygon, index: number): fabric.Point {
  const point = pointsOf(polygon)[index];
  const offset = polygon.pathOffset;
  return fabric.util.transformPoint(new fabric.Point(point.x - offset.x, point.y - offset.y), polygon.calcTransformMatrix());
}

/** Переносит точку `index` в `target` (координаты канвы); остальные точки остаются на канве где были. */
export function movePolygonPoint(polygon: Polygon, index: number, target: fabric.Point) {
  const points = pointsOf(polygon);
  const next = points.map((point, i) => (i === index ? toPolygonPoint(polygon, target) : point));
  reshape(polygon, next, anchorBesides(points.length, index), anchorBesides(points.length, index));
}

/** Добавляет точку на ребро `edgeIndex` (от точки `edgeIndex` к следующей) в место `target` на канве. */
export function insertPolygonPoint(polygon: Polygon, edgeIndex: number, target: fabric.Point) {
  const points = pointsOf(polygon);
  const next = [...points.slice(0, edgeIndex + 1), toPolygonPoint(polygon, target), ...points.slice(edgeIndex + 1)];
  reshape(polygon, next, 0, 0);
}

/** Удаляет точку `index`; у многоугольника остаётся не меньше `MIN_POLYGON_POINTS` — иначе `false`. */
export function removePolygonPoint(polygon: Polygon, index: number): boolean {
  const points = pointsOf(polygon);
  if (points.length <= MIN_POLYGON_POINTS) return false;
  const anchor = anchorBesides(points.length, index);
  const next = points.filter((_, i) => i !== index);
  reshape(polygon, next, anchor, anchor > index ? anchor - 1 : anchor);
  return true;
}

/** Куда попал `pointer` (координаты канвы): в точку, на ребро или мимо. `tolerance` — в координатах канвы. */
export function findPolygonHit(polygon: Polygon, pointer: fabric.Point, tolerance: number): PolygonHit | undefined {
  const onCanvas = pointsOf(polygon).map((_, index) => polygonPointOnCanvas(polygon, index));
  const pointIndex = onCanvas.findIndex((point) => point.distanceFrom(pointer) <= tolerance);
  if (pointIndex >= 0) return { kind: 'point', index: pointIndex };

  for (let index = 0; index < onCanvas.length; index++) {
    const projection = projectOnSegment(pointer, onCanvas[index], onCanvas[(index + 1) % onCanvas.length]);
    if (projection.distanceFrom(pointer) <= tolerance) return { kind: 'edge', index, point: projection };
  }
  return undefined;
}

/**
 * Режим «Изменить точки»: обычные ручки объекта меняются на ручки точек. Выход — `exit()`, Esc, снятие или смена
 * выделения, удаление объекта (в том числе «Отменить», которое перезагружает канву).
 */
class PolygonEdit {
  target = new ObservableField<Polygon | undefined>(undefined);

  private canvas?: Canvas;
  private options: PolygonEditOptions = {};
  private saved?: { controls: Polygon['controls']; hasBorders: Polygon['hasBorders']; padding: Polygon['padding'] };

  enter(polygon: Polygon, options: PolygonEditOptions = {}) {
    if (this.target.value === polygon) return;
    this.exit();
    const canvas = polygon.canvas;
    if (!canvas) return;

    this.canvas = canvas;
    this.options = options;
    this.saved = { controls: polygon.controls, hasBorders: polygon.hasBorders, padding: polygon.padding };
    polygon.hasBorders = false;
    // Ребро вдоль рамки: внешняя половина допуска лежит за объектом, и Fabric снял бы выделение (и режим) раньше
    // двойного клика. `padding` расширяет попадание по объекту (`lineCoords`, пиксели экрана) и не сериализуется.
    polygon.padding = HIT_TOLERANCE;
    this.target.value = polygon;
    this.applyControls(polygon);

    canvas.on('mouse:dblclick', this.onDblClick);
    canvas.on('selection:cleared', this.onLeave);
    canvas.on('selection:updated', this.onLeave);
    canvas.on('object:removed', this.onRemoved);
    document.addEventListener('keydown', this.onKeyDown);
  }

  exit() {
    const polygon = this.target.value;
    const canvas = this.canvas;
    if (!polygon || !canvas) return;

    canvas.off('mouse:dblclick', this.onDblClick);
    canvas.off('selection:cleared', this.onLeave);
    canvas.off('selection:updated', this.onLeave);
    canvas.off('object:removed', this.onRemoved);
    document.removeEventListener('keydown', this.onKeyDown);

    if (this.saved) {
      polygon.controls = this.saved.controls;
      polygon.hasBorders = this.saved.hasBorders;
      polygon.padding = this.saved.padding;
    }
    polygon.setCoords();
    this.saved = undefined;
    this.canvas = undefined;
    this.options = {};
    this.target.value = undefined;
    canvas.requestRenderAll();
  }

  toggle(polygon: Polygon, options: PolygonEditOptions = {}) {
    if (this.target.value === polygon) this.exit();
    else this.enter(polygon, options);
  }

  /** Добавить точку на ребре или удалить точку — один шаг истории. */
  editAt(pointer: fabric.Point) {
    const polygon = this.target.value;
    const canvas = this.canvas;
    if (!polygon || !canvas) return;

    const hit = findPolygonHit(polygon, pointer, HIT_TOLERANCE / canvas.getZoom());
    if (!hit) return;
    if (hit.kind === 'point' && pointsOf(polygon).length <= MIN_POLYGON_POINTS) return;

    history.batch(() => {
      this.track(polygon, () => {
        if (hit.kind === 'point') removePolygonPoint(polygon, hit.index);
        else insertPolygonPoint(polygon, hit.index, hit.point);
      });
      this.applyControls(polygon);
      canvas.fire('object:modified', { target: polygon });
    });
    canvas.requestRenderAll();
  }

  /** Своя форма вместо параметрической: поля трапеции, скоса и среза к ней уже не применимы. */
  private track(polygon: Polygon, change: () => void) {
    const from = { width: polygon.width ?? 0, height: polygon.height ?? 0 };
    change();
    const to = { width: polygon.width ?? 0, height: polygon.height ?? 0 };
    if (polygon.data?.shapeType) polygon.data = _.omit(polygon.data, 'shapeType');
    if (from.width !== to.width || from.height !== to.height) this.options.onResize?.(from, to);
  }

  private applyControls(polygon: Polygon) {
    polygon.controls = pointsOf(polygon).reduce<Polygon['controls']>((controls, _, index) => {
      controls[`p${index}`] = new fabric.Control({
        actionName: 'modifyPolygon',
        positionHandler: (_dim, _matrix, object) => {
          const target = object as Polygon;
          const vpt = target.canvas?.viewportTransform ?? [1, 0, 0, 1, 0, 0];
          // Точку только что удалили, ручки ещё прежние: `setCoords` внутри правки спрашивает и о ней.
          const point = index < pointsOf(target).length ? polygonPointOnCanvas(target, index) : target.getCenterPoint();
          return fabric.util.transformPoint(point, vpt);
        },
        actionHandler: (_event, transform, x, y) => {
          const target = transform.target as Polygon;
          this.track(target, () => movePolygonPoint(target, index, new fabric.Point(x, y)));
          return true;
        },
      });
      return controls;
    }, {});
    polygon.setCoords();
  }

  /**
   * `event.target` не проверяется: при `preserveObjectStacking` Fabric отдаёт верхний объект, и ребро под картинкой
   * или текстом было бы не достать. Попадание по точке и ребру считает `findPolygonHit` — мимо фигуры клик ничего не делает.
   */
  private onDblClick = (event: IEvent<Event>) => {
    if (!this.canvas) return;
    const { x, y } = this.canvas.getPointer(event.e as MouseEvent);
    this.editAt(new fabric.Point(x, y));
  };

  private onLeave = () => this.exit();

  private onRemoved = (event: IEvent<Event>) => {
    if (event.target === this.target.value) this.exit();
  };

  private onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') this.exit();
  };
}

const polygonEdit = new PolygonEdit();
export default polygonEdit;

function pointsOf(polygon: Polygon): fabric.Point[] {
  return (polygon.points ?? []) as fabric.Point[];
}

/** Точка канвы → координаты `points` многоугольника. */
function toPolygonPoint(polygon: Polygon, point: fabric.Point): fabric.Point {
  const local = fabric.util.transformPoint(point, fabric.util.invertTransform(polygon.calcTransformMatrix()));
  return new fabric.Point(local.x + polygon.pathOffset.x, local.y + polygon.pathOffset.y);
}

/** Якорь — соседняя неизменная точка (предыдущая, у первой — последняя). */
function anchorBesides(length: number, index: number) {
  return index > 0 ? index - 1 : length - 1;
}

/**
 * Пишет новые точки и пересчитывает рамку (`width` / `height` / `pathOffset` Fabric 5 считает только в
 * `initialize`), затем ставит объект так, чтобы якорь остался на канве там же — а с ним и все неизменные точки.
 */
function reshape(polygon: Polygon, next: fabric.Point[], anchorBefore: number, anchorAfter: number) {
  const anchorOnCanvas = polygonPointOnCanvas(polygon, anchorBefore);
  // Простые `{ x, y }`, как после `loadFromJSON`: `toObject` копирует массив поверхностно, и экземпляры
  // `fabric.Point` ушли бы в схему и в Redux (`savedScheme`) — несериализуемое значение.
  polygon.set({ points: next.map(({ x, y }) => ({ x, y }) as fabric.Point) });
  (polygon as Polygon & { _setPositionDimensions: (options: object) => void })._setPositionDimensions({});

  const anchor = next[anchorAfter];
  const offset = polygon.pathOffset;
  const fromCenter = fabric.util.transformPoint(
    new fabric.Point(anchor.x - offset.x, anchor.y - offset.y),
    polygon.calcTransformMatrix(),
    true,
  );
  polygon.setPositionByOrigin(anchorOnCanvas.subtract(fromCenter), 'center', 'center');
  polygon.setCoords();
  polygon.dirty = true;
}

function projectOnSegment(point: fabric.Point, a: fabric.Point, b: fabric.Point): fabric.Point {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSq = dx * dx + dy * dy;
  if (!lengthSq) return new fabric.Point(a.x, a.y);
  const t = Math.min(Math.max(((point.x - a.x) * dx + (point.y - a.y) * dy) / lengthSq, 0), 1);
  return new fabric.Point(a.x + t * dx, a.y + t * dy);
}
